import { Button, Card, CardProps, Collapse } from "@blueprintjs/core";
import { FC, ReactNode, useState } from "react";
import { SecondaryCardHeader } from "./SecondaryCardHeader";
import classNames from "classnames";
import { UCLColorKey } from "../colors/uclColors";

export interface SecondaryCardProps extends Omit<CardProps, "elevation"> {
  headerContent?: string | ReactNode | undefined;
  hideBody?: boolean;
  isCollapsible?: boolean;
  columns?: number;
  children?: ReactNode | undefined;
  className?: string;
  selected?: boolean;
  onSelect?: () => void;
  color?: UCLColorKey;
}

/**
 * Used Only As A Nested Child of Relativity Card
 * `RelativitySubCard` is a wrapper around @blueprintjs/core's `Card` component.
 *
 * This wrapper adds header & body capabilities.
 */
const SecondaryCard: FC<SecondaryCardProps> = ({
  headerContent,
  hideBody,
  isCollapsible,
  columns,
  children,
  selected,
  onSelect,
  color,
  ...cardProps
}) => {
  const [isOpen, setIsOpen] = useState(!hideBody);

  return (
    <div
      className={classNames(
        "ucl-subcard ucl-subcard--secondary",
        { "is-collapsible": isCollapsible },
        { "ucl-subcard--selected": selected },
        { "ucl-subcard--clickable": !!onSelect },
        color ? `ucl-subcard--color-${color}` : null
      )}
      onClick={onSelect}
    >
      <Card {...cardProps}>
        <div className={classNames("ucl-subcard__header-container", { "ucl-subcard__header-container--no-header": !headerContent })}>
          {isCollapsible && (
            <Button
              className="collapse-toggle"
              icon={isOpen ? "chevron-down" : "chevron-right"}
              minimal={true}
              onClick={e => {
                e.stopPropagation();
                setIsOpen((open) => !open);
              }}
            />
          )}
          {!!headerContent &&
            (typeof headerContent === "string" ||
              headerContent instanceof String) && (
              <SecondaryCardHeader label={headerContent as string} />
            )}
          {!!headerContent &&
            !(
              typeof headerContent === "string" ||
              headerContent instanceof String
            ) && (
              <section className="ucl-subcard__header">{headerContent}</section>
            )}
        </div>
        <Collapse isOpen={isOpen}>
          <div
            className={`ucl-card__body ucl-card__body--secondary ${
              columns ? "ucl-grid ucl-grid-" + columns : ""
            }`}
          >
            {children}
          </div>
          <div className="ucl-card__footer-block"></div>
        </Collapse>
      </Card>
    </div>
  );
};

export default SecondaryCard;
